"use client";

import { useEffect, useState, useRef } from "react";
import { cn } from "@/lib/utils";
import {
  Book,
  Flame,
  Swords,
  Crown,
  Moon,
  Ghost,
  Key,
  FileText,
  PlusCircle,
} from "lucide-react";

/* ── Fake data ─────────────────────────────────── */

const THREADS = [
  {
    title: "The Awakening Arc",
    color: "bg-indigo-500",
    eventCount: 4,
    events: [
      { icon: Book, label: "Prologue", color: "indigo" },
      { icon: Flame, label: "The Blaze", color: "indigo" },
      { icon: Swords, label: "First Battle", color: "red" },
      { icon: Crown, label: "Coronation", color: "amber" },
    ],
  },
  {
    title: "The Shadow Path",
    color: "bg-purple-500",
    eventCount: 3,
    events: [
      { icon: Moon, label: "Nightfall", color: "purple" },
      { icon: Ghost, label: "The Specter", color: "purple" },
      { icon: Key, label: "Secret Found", color: "emerald" },
    ],
  },
];

/* ── Color lookups (static for Tailwind JIT) ───── */

const NODE_STYLES: Record<
  string,
  { active: string; inactive: string; icon: string; iconActive: string }
> = {
  indigo: {
    active:
      "bg-indigo-500 border-white ring-4 ring-indigo-500/20 shadow-md shadow-indigo-500/30",
    inactive:
      "bg-white dark:bg-[#0B0F19] border-indigo-500 dark:border-indigo-500/40",
    icon: "text-indigo-500 dark:text-indigo-400",
    iconActive: "text-white",
  },
  purple: {
    active:
      "bg-purple-500 border-white ring-4 ring-purple-500/20 shadow-md shadow-purple-500/30",
    inactive:
      "bg-white dark:bg-[#0B0F19] border-purple-500 dark:border-purple-500/40",
    icon: "text-purple-500 dark:text-purple-400",
    iconActive: "text-white",
  },
  red: {
    active:
      "bg-red-500 border-white ring-4 ring-red-500/20 shadow-md shadow-red-500/30",
    inactive:
      "bg-white dark:bg-[#0B0F19] border-red-500 dark:border-red-500/40",
    icon: "text-red-500 dark:text-red-400",
    iconActive: "text-white",
  },
  amber: {
    active:
      "bg-amber-500 border-white ring-4 ring-amber-500/20 shadow-md shadow-amber-500/30",
    inactive:
      "bg-white dark:bg-[#0B0F19] border-amber-500 dark:border-amber-500/40",
    icon: "text-amber-500 dark:text-amber-400",
    iconActive: "text-white",
  },
  emerald: {
    active:
      "bg-emerald-500 border-white ring-4 ring-emerald-500/20 shadow-md shadow-emerald-500/30",
    inactive:
      "bg-white dark:bg-[#0B0F19] border-emerald-500 dark:border-emerald-500/40",
    icon: "text-emerald-500 dark:text-emerald-400",
    iconActive: "text-white",
  },
};

/* ── Component ─────────────────────────────────── */

export function PlotThreadsDemo() {
  const [step, setStep] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;

    const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

    async function runAnimation() {
      const isCancelled = () => cancelledRef.current;

      setStep(0);
      await wait(400);
      if (isCancelled()) return;

      // Step 1: First thread title appears
      setStep(1);
      await wait(800);
      if (isCancelled()) return;

      // Step 2: First thread events pop in
      setStep(2);
      await wait(1200);
      if (isCancelled()) return;

      // Step 3: Second thread appears with events
      setStep(3);
      await wait(1200);
      if (isCancelled()) return;

      // Step 4: Select an event – show document sidebar
      setStep(4);
      await wait(1000);
      if (isCancelled()) return;

      // Step 5: Document content types in
      setStep(5);
      await wait(6000);
      if (isCancelled()) return;

      if (!isCancelled()) runAnimation();
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          runAnimation();
        }
      },
      { threshold: 0.4 }
    );

    if (containerRef.current) observer.observe(containerRef.current);

    return () => {
      cancelledRef.current = true;
      observer.disconnect();
    };
  }, []);

  /* Selected event index for highlight (event 2 = "First Battle") */
  const selectedThread = 0;
  const selectedEvent = 2;

  return (
    <section
      ref={containerRef}
      className="mx-auto flex w-full max-w-6xl flex-col items-center justify-between gap-12 px-6 pt-24 pb-12 md:flex-row-reverse lg:px-12"
    >
      {/* ── Visual Demo Container ──────────────── */}
      <div className="relative w-full max-w-2xl flex-1 overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-bl from-indigo-900/20 via-background to-purple-900/10 p-5 shadow-2xl before:pointer-events-none before:absolute before:inset-0 before:bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] before:from-indigo-500/10 before:via-transparent before:to-transparent">
        {/* Subtle grid background */}
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-[size:20px_20px] opacity-[0.03]" />

        <div className="relative space-y-6">
          {/* ── Thread 1 ─────────────────────── */}
          <div
            className={cn(
              "transition-all duration-700",
              step >= 1
                ? "translate-y-0 opacity-100"
                : "translate-y-4 opacity-0"
            )}
          >
            {/* Thread header */}
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="h-6 w-1.5 shrink-0 rounded-full bg-indigo-500" />
                <span className="font-heading text-sm font-bold text-foreground sm:text-base">
                  {THREADS[0].title}
                </span>
              </div>
              <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                {THREADS[0].eventCount} Events
              </span>
            </div>

            {/* Timeline track */}
            <div className="relative flex items-center overflow-hidden py-3">
              {/* Dashed line */}
              <div className="absolute top-1/2 right-0 left-0 h-0.5 -translate-y-1/2 border-t-2 border-dashed border-border" />

              <div className="relative z-10 flex w-full items-center gap-6 px-4 sm:gap-10 sm:px-8">
                {THREADS[0].events.map((evt, i) => {
                  const isSelected =
                    step >= 4 &&
                    selectedThread === 0 &&
                    selectedEvent === i;
                  const style = NODE_STYLES[evt.color] || NODE_STYLES.indigo;
                  const Icon = evt.icon;

                  return (
                    <div
                      key={evt.label}
                      className={cn(
                        "flex flex-col items-center transition-all duration-500",
                        step >= 2
                          ? "scale-100 opacity-100"
                          : "scale-0 opacity-0"
                      )}
                      style={{ transitionDelay: `${i * 120}ms` }}
                    >
                      <div
                        className={cn(
                          "flex h-11 w-11 items-center justify-center rounded-full border-2 transition-all duration-300 sm:h-12 sm:w-12",
                          isSelected ? style.active : style.inactive
                        )}
                      >
                        <Icon
                          className={cn(
                            "size-4 sm:size-5",
                            isSelected ? style.iconActive : style.icon
                          )}
                        />
                      </div>
                      <span
                        className={cn(
                          "mt-1.5 max-w-[60px] truncate text-center text-[9px] font-semibold uppercase tracking-wider sm:text-[10px]",
                          isSelected
                            ? "text-foreground"
                            : "text-muted-foreground"
                        )}
                      >
                        {evt.label}
                      </span>
                    </div>
                  );
                })}
                {/* Add button */}
                <div
                  className={cn(
                    "transition-all duration-500",
                    step >= 2
                      ? "scale-100 opacity-100"
                      : "scale-0 opacity-0"
                  )}
                  style={{ transitionDelay: "480ms" }}
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-dashed border-border text-muted-foreground sm:h-10 sm:w-10">
                    <PlusCircle className="size-4" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Thread 2 ─────────────────────── */}
          <div
            className={cn(
              "transition-all duration-700",
              step >= 3
                ? "translate-y-0 opacity-100"
                : "translate-y-4 opacity-0"
            )}
          >
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="h-6 w-1.5 shrink-0 rounded-full bg-purple-500" />
                <span className="font-heading text-sm font-bold text-foreground sm:text-base">
                  {THREADS[1].title}
                </span>
              </div>
              <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                {THREADS[1].eventCount} Events
              </span>
            </div>

            <div className="relative flex items-center overflow-hidden py-3">
              <div className="absolute top-1/2 right-0 left-0 h-0.5 -translate-y-1/2 border-t-2 border-dashed border-border" />

              <div className="relative z-10 flex w-full items-center gap-6 px-4 sm:gap-10 sm:px-8">
                {THREADS[1].events.map((evt, i) => {
                  const style = NODE_STYLES[evt.color] || NODE_STYLES.purple;
                  const Icon = evt.icon;

                  return (
                    <div
                      key={evt.label}
                      className={cn(
                        "flex flex-col items-center transition-all duration-500",
                        step >= 3
                          ? "scale-100 opacity-100"
                          : "scale-0 opacity-0"
                      )}
                      style={{ transitionDelay: `${i * 150 + 300}ms` }}
                    >
                      <div
                        className={cn(
                          "flex h-11 w-11 items-center justify-center rounded-full border-2 transition-all duration-300 sm:h-12 sm:w-12",
                          style.inactive
                        )}
                      >
                        <Icon className={cn("size-4 sm:size-5", style.icon)} />
                      </div>
                      <span className="mt-1.5 max-w-[60px] truncate text-center text-[9px] font-semibold uppercase tracking-wider text-muted-foreground sm:text-[10px]">
                        {evt.label}
                      </span>
                    </div>
                  );
                })}
                <div
                  className={cn(
                    "transition-all duration-500",
                    step >= 3
                      ? "scale-100 opacity-100"
                      : "scale-0 opacity-0"
                  )}
                  style={{ transitionDelay: "750ms" }}
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-dashed border-border text-muted-foreground sm:h-10 sm:w-10">
                    <PlusCircle className="size-4" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Document Sidebar Preview ──────── */}
          <div
            className={cn(
              "overflow-hidden rounded-2xl border border-border/50 bg-card/80 shadow-lg backdrop-blur-xl transition-all duration-700",
              step >= 4
                ? "max-h-96 translate-y-0 opacity-100 sm:max-h-48"
                : "max-h-0 translate-y-4 opacity-0"
            )}
          >
            <div className="border-b border-border/50 bg-muted/30 px-4 py-2">
              <div className="flex items-center gap-2 text-xs font-semibold text-foreground">
                <FileText className="size-3.5 text-red-400" />
                <span>First Battle</span>
                <span className="ml-auto rounded-full bg-red-500/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-red-400">
                  Swords
                </span>
              </div>
            </div>
            <div className="space-y-2 p-4">
              <p
                className={cn(
                  "font-serif text-xs leading-relaxed text-muted-foreground transition-opacity duration-1000",
                  step >= 5 ? "opacity-100" : "opacity-0"
                )}
              >
                The clash of steel echoed through the valley as Arthur raised his
                blade for the first time. Beside him, @<span className="rounded bg-indigo-500/15 px-1 font-semibold text-indigo-400">charlie</span> stood ready, his
                shield catching the torchlight...
              </p>
              <div
                className={cn(
                  "flex items-center gap-2 transition-opacity duration-700",
                  step >= 5 ? "opacity-100" : "opacity-0"
                )}
                style={{ transitionDelay: "400ms" }}
              >
                <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">
                  Mentions:
                </span>
                <span className="rounded-full border border-indigo-500/30 bg-indigo-500/10 px-2 py-0.5 text-[9px] font-semibold text-indigo-400">
                  charlie
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Text Explainer ─────────────────────── */}
      <div className="max-w-lg flex-1 space-y-6">
        <h2 className="font-heading text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
          Plot your story across timelines
        </h2>
        <p className="text-lg leading-relaxed text-muted-foreground">
          Create narrative arcs with branching events, sub-plots, and scene
          breakdowns. Every event has its own rich document — write directly
          inside the timeline.
        </p>

        <ul className="mt-8 space-y-4">
          <li
            className={cn(
              "flex items-start gap-3 transition-opacity duration-700",
              step >= 1 ? "opacity-100" : "opacity-0"
            )}
            style={{ transitionDelay: "600ms" }}
          >
            <div className="mt-1 shrink-0 rounded-full bg-indigo-500/20 p-1 text-indigo-400">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <div>
              <span className="font-semibold text-foreground">
                Multiple arcs:
              </span>{" "}
              Run parallel storylines — main plot, subplots, and character arcs
              side by side.
            </div>
          </li>
          <li
            className={cn(
              "flex items-start gap-3 transition-opacity duration-700",
              step >= 2 ? "opacity-100" : "opacity-0"
            )}
            style={{ transitionDelay: "800ms" }}
          >
            <div className="mt-1 shrink-0 rounded-full bg-purple-500/20 p-1 text-purple-400">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <div>
              <span className="font-semibold text-foreground">
                Events with documents:
              </span>{" "}
              Each event holds a rich-text scene — write prose right inside the
              timeline node.
            </div>
          </li>
          <li
            className={cn(
              "flex items-start gap-3 transition-opacity duration-700",
              step >= 3 ? "opacity-100" : "opacity-0"
            )}
            style={{ transitionDelay: "1000ms" }}
          >
            <div className="mt-1 shrink-0 rounded-full bg-rose-500/20 p-1 text-rose-400">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <div>
              <span className="font-semibold text-foreground">
                Color-coded & iconic:
              </span>{" "}
              Tag events with 30+ icons and custom colors to see your story at a
              glance.
            </div>
          </li>
        </ul>
      </div>
    </section>
  );
}
